import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { FaEnvelope, FaXTwitter } from "react-icons/fa6";
import { getPublicJobById } from "../services/publicService";
import { AuthModal } from "./AuthModal";
import "./JobDetailsPage.css";

const formatJobDescription = (description = "") => {
  const normalizedDescription = description.replace(/\r\n/g, "\n").trim();

  if (!normalizedDescription) {
    return [];
  }

  return normalizedDescription
    .split(/\n{2,}/)
    .map((section) => section.trim())
    .filter(Boolean);
};

const toTitleCase = (value = "") =>
  value.toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase());

const formatJobLocation = (location = "") => {
  const city = toTitleCase(location.trim());

  if (!city) {
    return "";
  }

  if (city.includes(",")) {
    return city
      .split(",")
      .map((part) => toTitleCase(part.trim()))
      .filter(Boolean)
      .join(", ");
  }

  const cityStateMap = {
    Hyderabad: "Telangana",
    Kochi: "Kerala",
    Bangalore: "Karnataka",
    Bengaluru: "Karnataka",
    Chennai: "Tamil Nadu",
    Mumbai: "Maharashtra",
  };

  const state = cityStateMap[city] || "Telangana";

  return `${city}, ${state}, India`;
};

const formatJobPostedDate = (createdAt) => {
  if (!createdAt) {
    return "";
  }

  return new Date(createdAt).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const JobDetailsPage = () => {
  const { jobId } = useParams();

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  useEffect(() => {
    const fetchJob = async () => {
      try {
        const res = await getPublicJobById(jobId);

        setJob(res.job);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchJob();
  }, [jobId]);

  const descriptionSections = formatJobDescription(job?.description);
  const jobLocation = formatJobLocation(job?.location);
  const jobProfileType = "Professional";

  const getPublicJobUrl = () => {
    if (typeof window === "undefined") {
      return "";
    }

    return `${window.location.origin}/careers/${job?.id || jobId}`;
  };

  const openShareWindow = (shareUrl) => {
    if (typeof window === "undefined") {
      return;
    }

    window.open(shareUrl, "_blank", "noopener,noreferrer,width=720,height=640");
  };

  // const handleEmailShare = () => {
  //   const publicUrl = getPublicJobUrl();
  //   const subject = encodeURIComponent(job.title);
  //   const body = encodeURIComponent(`Check this job opening: ${publicUrl}`);

  //   window.location.href = `mailto:?subject=${subject}&body=${body}`;
  // };

  const handleEmailShare = () => {
    const publicUrl = `https://blackroth.in/careers/${job.id}`;
    const subject = encodeURIComponent(job.title);
    const body = encodeURIComponent(`Check this job opening: ${publicUrl}`);
    const outlookWebUrl = `https://outlook.office.com/mail/deeplink/compose?subject=${subject}&body=${body}`;

    window.open(outlookWebUrl, "_blank");
  };

  const handleTwitterShare = () => {
    const text = encodeURIComponent(job.title);
    const url = encodeURIComponent(getPublicJobUrl());

    openShareWindow(`https://twitter.com/intent/tweet?text=${text}&url=${url}`);
  };

  const handleLinkedInShare = () => {
    const encodedUrl = encodeURIComponent(getPublicJobUrl());

    openShareWindow(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    );
  };

  if (loading) {
    return (
      <div className="public-job-details">
        <div className="public-job-state">
          <h2>Loading job details...</h2>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="public-job-details">
        <div className="public-job-state">
          <h2>Job not found</h2>
          <Link to="/careers" className="public-job-back-link">
            &lt; Back to careers
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="public-job-details">
      <section className="public-job-hero">
        <div className="public-job-inner">
          <div className="public-job-nav-links">
            <Link to="/" className="public-job-back-link">
              &lt; Back to home
            </Link>

            <Link to="/careers" className="public-job-back-link">
              &lt; Back to careers
            </Link>
          </div>

          <div className="public-job-heading">
            <h1 className="public-job-title">{job.title}</h1>

            <div className="public-job-meta-row" aria-label="Job information">
              {jobLocation && <span>{jobLocation}</span>}

              <span>{jobProfileType}</span>

              <div className="public-job-share-actions" aria-label="Share job">
                <button
                  type="button"
                  className="public-job-share-btn public-job-mail-share"
                  aria-label="Share by email"
                  onClick={handleEmailShare}
                >
                  <FaEnvelope aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="public-job-share-btn public-job-x-share"
                  aria-label="Share on X"
                  onClick={handleTwitterShare}
                >
                  <FaXTwitter aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="public-job-share-btn public-job-linkedin-share"
                  aria-label="Share on LinkedIn"
                  onClick={handleLinkedInShare}
                >
                  in
                </button>
              </div>
            </div>
          </div>

          <button
            className="public-job-apply-btn"
            onClick={() => setIsAuthModalOpen(true)}
          >
            Apply now
          </button>
        </div>
      </section>

      <section className="public-job-body">
        <div className="public-job-inner">
          <div className="public-job-grid">
            <div className="public-job-description-block">
              <h2 className="public-job-desc-title">Introduction</h2>

              <div className="public-job-description-content">
                {descriptionSections.length > 0 ? (
                  descriptionSections.map((section, index) => (
                    <p
                      key={`${job.id || jobId}-description-${index}`}
                      className="public-job-description"
                    >
                      {section}
                    </p>
                  ))
                ) : (
                  <p className="public-job-description">
                    No description available.
                  </p>
                )}
              </div>
            </div>

            <aside className="public-job-summary" aria-label="Job summary">
              <div className="public-job-summary-item">
                <h2>Job Title</h2>
                <p>{job.title}</p>
              </div>

              <div className="public-job-summary-item">
                <h2>Job ID</h2>
                <p>{job.job_code}</p>
              </div>

              <div className="public-job-summary-item">
                <h2>Job Experience</h2>
                <p>{job.experience}</p>
              </div>

              <div className="public-job-summary-item">
                <h2>Job POSTED ON</h2>
                <p>{formatJobPostedDate(job.createdAt)}</p>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
      />
    </div>
  );
};

export default JobDetailsPage;
